import React from 'react';
import { useGetOne } from 'react-admin';
import { getChainId } from '../../../../lib';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Paper,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import CasinoIcon from '@mui/icons-material/Casino';
import PaymentsIcon from '@mui/icons-material/Payments';
import { GiftProductType } from '../../../../models';

interface ProductTypeOption {
  type: GiftProductType;
  // Recompensa em dinheiro: mesmo product_type do produto virtual, com o valor de face
  // no lugar do catálogo. É uma opção de cadastro, não um tipo novo no banco.
  cashback?: boolean;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}

interface ProductTypeSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelectType: (type: GiftProductType, options?: { cashback?: boolean }) => void;
  // Tipos de produto a ocultar. Ex: quando o brinde já tem regra prediction_winner
  // ("Acertou Bolão"), escondemos PredictionTicket — "acertou bolão → ganha palpite"
  // confunde o usuário. Ver gift B / bolão.
  excludedTypes?: GiftProductType[];
  // Esconde a recompensa em dinheiro. Com a regra "Troca por Pontos" no brinde ela seria
  // saque (paga pontos, recebe dinheiro) — o backend recusa o cadastro.
  hideCashback?: boolean;
}

const ProductTypeSelectionModal: React.FC<ProductTypeSelectionModalProps> = ({
  open,
  onClose,
  onSelectType,
  excludedTypes = [],
  hideCashback = false,
}) => {
  // Rede sem módulo de bolão habilitado não vê opção "Direito ao Palpite".
  // Mesma ideia para sorteios: sem o módulo, não há sorteio para vincular.
  const chainId = getChainId();
  const { data: chain } = useGetOne('chains', { id: chainId }, { enabled: !!chainId });
  const bolaoDisabled = !chain?.useBolao;
  const drawingDisabled = !chain?.useDrawing;
  // Mesma ideia para a Roleta da Sorte: sem o módulo não há roleta para vincular.
  const roletaDisabled = !chain?.useRoleta;
  const allProductTypes: ProductTypeOption[] = [
    {
      type: GiftProductType.Virtual,
      title: 'Produto Virtual',
      description: 'Crie um produto customizado com nome, descrição, valor e imagem. Ideal para recompensas únicas ou promocionais.',
      icon: <CardGiftcardIcon sx={{ fontSize: 40 }} />,
      color: '#1976d2',
      bgColor: '#e3f2fd',
    },
    {
      type: GiftProductType.Virtual,
      cashback: true,
      title: 'Cashback (Vale em Dinheiro)',
      description: 'Dá ao cliente um valor em reais para usar na próxima compra. Só precisa do valor: o cupom vale em qualquer local da rede que aceite cashback e o app mostra o valor no lugar da imagem.',
      icon: <PaymentsIcon sx={{ fontSize: 40 }} />,
      color: '#2e7d32',
      bgColor: '#e8f5e9',
    },
    {
      type: GiftProductType.Existing,
      title: 'Produto Existente',
      description: 'Selecione um produto já cadastrado no sistema. Vincula ao estoque e informações existentes.',
      icon: <InventoryIcon sx={{ fontSize: 40 }} />,
      color: '#7b1fa2',
      bgColor: '#f3e5f5',
    },
    {
      type: GiftProductType.FuelDiscount,
      title: 'Desconto no Abastecimento',
      description: 'Configure um desconto por litro para combustíveis específicos. O desconto é aplicado diretamente na bomba.',
      icon: <LocalGasStationIcon sx={{ fontSize: 40 }} />,
      color: '#f57c00',
      bgColor: '#fff3e0',
    },
    {
      type: GiftProductType.PredictionTicket,
      title: 'Direito ao Palpite (Bolão)',
      description: 'Dá ao cliente o direito de palpitar em um bolão que você já cadastrou. Ele só receberá o cupom de verdade se acertar o placar exato.',
      icon: <SportsSoccerIcon sx={{ fontSize: 40 }} />,
      color: '#7869DF',
      bgColor: '#ede9fe',
    },
    {
      type: GiftProductType.DrawingNumber,
      title: 'Número da Sorte (Sorteio)',
      description: 'Dá ao cliente números da sorte de um sorteio que você já cadastrou. Só vale para sorteios com as regras no Clube de Vantagens.',
      icon: <CasinoIcon sx={{ fontSize: 40 }} />,
      color: '#00897b',
      bgColor: '#e0f2f1',
    },
    {
      type: GiftProductType.SpinTicket,
      title: 'Giro na Roleta',
      description: 'Dá ao cliente giros na Roleta da Sorte que você já cadastrou. O prêmio sai na hora do giro, escolhido pela roda.',
      icon: <CasinoIcon sx={{ fontSize: 40 }} />,
      color: '#7869DF',
      bgColor: '#ede9fe',
    },
  ];

  const productTypes = allProductTypes.filter(
    (option) =>
      !excludedTypes.includes(option.type) &&
      !(hideCashback && option.cashback) &&
      !(bolaoDisabled && option.type === GiftProductType.PredictionTicket) &&
      !(drawingDisabled && option.type === GiftProductType.DrawingNumber) &&
      !(roletaDisabled && option.type === GiftProductType.SpinTicket),
  );

  const handleSelectType = (type: GiftProductType, options?: { cashback?: boolean }) => {
    onSelectType(type, options);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            Selecione o tipo de produto
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} py={1}>
          {productTypes.map((option) => (
            <Paper
              key={option.cashback ? `${option.type}-cashback` : option.type}
              elevation={0}
              sx={{
                p: 2.5,
                cursor: 'pointer',
                border: '2px solid',
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: option.color,
                  bgcolor: option.bgColor,
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                },
              }}
              onClick={() => handleSelectType(option.type, { cashback: !!option.cashback })}
            >
              <Box display="flex" gap={2}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    bgcolor: option.bgColor,
                    color: option.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {option.icon}
                </Box>
                <Box flex={1}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {option.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                    {option.description}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ProductTypeSelectionModal;
